import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { AddonServiceRuleItemsData } from '../../../common/types/rates.types';
import AddonServiceRulesForm from './AddonServiceRulesForm';
import {
  getAccommodationAddOnsAsync,
  selectAccommodationAddOns,
  selectAccommodationAddOnsLoadingStatus,
} from '../../accommodations/accommodationsSlice';
import {
  addAdditionalServiceRule,
  getDefaultRateAdditionalServiceRulesDataAsync,
  removeAdditionalServiceRule,
  selectAdditionalServiceRulesItemsData,
  selectAdditionalServiceRulesLoadingStatus,
  updateAdditionalServiceField,
  updateDefaultRateAdditionalServiceRulesAsync,
} from '../defaultRateRulesSlice';
import SeasonRatePickerContainer from '../../seasonRates/SeasonRatePickerContainer';
import { copyAdditionalServiceRulesToSeasons } from '../../defaultRateTools/defaultRateToolsSlice';

function DefaultRateAddonServiceRules({
  defaultRateId,
  accommodationId,
  hasWriteAccess,
}: {
  defaultRateId: string;
  accommodationId: string;
  hasWriteAccess: boolean;
}) {
  const dispatch = useAppDispatch();
  const addonServiceRuleItemsData = useAppSelector(
    selectAdditionalServiceRulesItemsData,
  );
  const accommodationAddons = useAppSelector(selectAccommodationAddOns);
  const addonServiceRuleItemsDataStatus = useAppSelector(
    selectAdditionalServiceRulesLoadingStatus,
  );
  const accommodationAddonsStatus = useAppSelector(
    selectAccommodationAddOnsLoadingStatus,
  );
  const [selectedSeasonRateIds, setSelectedSeasonRateIds] = useState<string[]>(
    [],
  );

  const initialData: AddonServiceRuleItemsData = {
    defaultRateId,
    additionalServiceRules: [],
  };

  const [data, setData] = useState(initialData);
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    dispatch(getAccommodationAddOnsAsync(accommodationId));
    dispatch(
      getDefaultRateAdditionalServiceRulesDataAsync({
        defaultRateId,
        accommodationId,
      }),
    );
  }, [defaultRateId, accommodationId]);

  useEffect(() => {
    if (addonServiceRuleItemsData && addonServiceRuleItemsData.defaultRateId) {
      setData(addonServiceRuleItemsData);
    } else {
      setData(initialData);
    }
  }, [addonServiceRuleItemsData]);

  useEffect(() => {}, [data]);

  const handleInputChange =
    (index: string, field: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { type, checked, value } = event.target;
      dispatch(
        updateAdditionalServiceField({
          index,
          field,
          value: type === 'checkbox' ? checked : value,
        }),
      );
    };

  const handleSelectChange = (selectedOption, index, field) => {
    dispatch(
      updateAdditionalServiceField({
        index,
        field,
        value: selectedOption ? selectedOption.value : null,
      }),
    );
  };

  const handleAddRule = (addonDefinitionId: string, addonName: string) => {
    const newRule = {
      additionalServiceId: addonDefinitionId,
      additionalServiceName: addonName,
      isMandatory: true,
      priceChange: {
        priceChangeType: 2,
        difference: 0,
        percent: 0,
      },
      isCopyable: true,
    };
    dispatch(addAdditionalServiceRule(newRule));
  };

  const handleRemoveRule = index => {
    dispatch(removeAdditionalServiceRule(index));
  };

  const onSubmit = () => {
    dispatch(updateDefaultRateAdditionalServiceRulesAsync(data));
    setSubmitted(true);
  };

  const handleCopyingForSeasonRates = async () => {
    if (selectedSeasonRateIds.length > 0) {
      dispatch(
        copyAdditionalServiceRulesToSeasons({
          accommodationId,
          selectedSeasonRateIds,
          additionalServiceRules: data.additionalServiceRules,
        }),
      )
        .unwrap()
        .then(() => {
          setCopied(true);
        });
    }
  };

  const handleSeasonRateIds = (selectedIds: string[]) => {
    setSelectedSeasonRateIds(selectedIds);
  };

  return (
    <>
      <Row sm={1}>
        <Row sm={1}>
          <Col>
            <Form.Label>Valitse kohdekaudet</Form.Label>
          </Col>
        </Row>
        <Col>
          <SeasonRatePickerContainer
            selectedAccommodationId={accommodationId}
            onSelectedIdsChange={handleSeasonRateIds}
          />
        </Col>
      </Row>
      <AddonServiceRulesForm
        loadingStatus={addonServiceRuleItemsDataStatus}
        addonsLoadingStatus={accommodationAddonsStatus}
        handleInputChange={handleInputChange}
        handleSelectChange={handleSelectChange}
        onSubmit={onSubmit}
        submitted={submitted}
        data={data}
        accommodationAddons={accommodationAddons}
        hasWriteAccess={hasWriteAccess}
        handleAddRule={handleAddRule}
        handleRemoveRule={handleRemoveRule}
        isDefaultRate
      />
      <Form.Group className="mb-24">
        <Button
          variant="outline-blue"
          className="mt-16 mb-16 w-100"
          onClick={handleCopyingForSeasonRates}
        >
          Kopioi sääntö hintakausille
        </Button>
        {copied && (
          <div className="mt-2 pt-2">
            <Alert variant="success">Sääntö kopioitu hintakausille!</Alert>
          </div>
        )}
      </Form.Group>
    </>
  );
}

export default DefaultRateAddonServiceRules;
